import { addMonths, differenceInCalendarMonths, isBefore, isSameDay, startOfMonth, subMonths } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MonthCalendar } from "@/components/atomic/calendar/MonthCalendar";
import { type DateRange } from "@/components/atomic/calendar/types";
import { cn } from "@/lib/utils";

interface RangeCalendarProps {
  className?: string;
  onCancel?: () => void;
  onConfirm?: (range: DateRange) => void;
  initialDateRange?: DateRange;
  initialLeftMonth?: Date;
  initialRightMonth?: Date;
}

export const RangeCalendar = (props: Readonly<RangeCalendarProps>) => {
  const { className, onCancel, onConfirm, initialDateRange, initialLeftMonth, initialRightMonth } = props;
  const { t } = useTranslation("common");
  const today = useMemo(() => new Date(), []);

  // 初期表示月の計算
  const getInitialMonths = () => {
    let left: Date;
    let right: Date;

    if (initialDateRange?.from && initialDateRange?.to) {
      const startMonth = initialDateRange.from;
      const endMonth = initialDateRange.to;

      // 開始日と終了日が同じ月の場合
      if (
        startMonth.getMonth() === endMonth.getMonth()
        && startMonth.getFullYear() === endMonth.getFullYear()
      ) {
        left = subMonths(endMonth, 1);
        right = endMonth;
      } else {
        // 異なる月の場合
        left = startMonth;
        right = endMonth;
      }
    } else {
      // デフォルト: 前月と今月
      left = subMonths(today, 1);
      right = today;
    }

    // propsで渡された値で上書き
    if (initialLeftMonth) {
      left = initialLeftMonth;
    }
    if (initialRightMonth) {
      right = initialRightMonth;
    }

    return { left, right };
  };

  const initialMonths = getInitialMonths();
  const [leftMonth, setLeftMonth] = useState(initialMonths.left);
  const [rightMonth, setRightMonth] = useState(initialMonths.right);
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange || { from: null, to: null });
  const isLeftNextDisabled = useMemo(() => {
    const nextMonth = startOfMonth(addMonths(leftMonth, 1));
    return differenceInCalendarMonths(nextMonth, rightMonth) >= 0; // nextMonth >= rightMonth
  }, [leftMonth, rightMonth]);

  const isRightPrevDisabled = useMemo(() => {
    const prevMonth = startOfMonth(subMonths(rightMonth, 1));
    return differenceInCalendarMonths(prevMonth, leftMonth) <= 0; // prevMonth <= leftMonth
  }, [rightMonth, leftMonth]);

  const handleDateClick = useCallback((date: Date) => {
    setDateRange(current => {
      if (current.from && current.to && isSameDay(current.from, current.to) && isSameDay(date, current.from)) {
        const newRange: DateRange = { from: null, to: null };
        return newRange;
      }

      let newRange: DateRange;

      if (!current.from || current.to) {
        newRange = { from: date, to: null };
      } else if (isBefore(date, current.from)) {
        // 選択した日付を開始日とし、元の開始日を終了日にする（終了日は23:59:59に設定）
        const endDate = new Date(current.from);
        endDate.setHours(23, 59, 59, 999);
        newRange = { from: date, to: endDate };
      } else {
        // 終了日を23:59:59に設定
        const endDate = new Date(date);
        endDate.setHours(23, 59, 59, 999);
        newRange = { from: current.from, to: endDate };
      }

      return newRange;
    });
  }, []);

  const handleCancel = useCallback(() => {
    setDateRange({ from: null, to: null });
    onCancel?.();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    onConfirm?.(dateRange);
  }, [onConfirm, dateRange]);

  return (
    <div
      className={cn(
        "bg-white rounded-[5px] shadow-[0px_0px_10px_0px_rgba(41,61,114,0.15)] p-3 flex flex-col gap-2 w-fit",
        className,
      )}
    >
      <div className="flex gap-6">
        <MonthCalendar
          currentMonth={leftMonth}
          onPrevMonth={() => setLeftMonth(subMonths(leftMonth, 1))}
          onNextMonth={() => setLeftMonth(addMonths(leftMonth, 1))}
          rangeStart={dateRange.from}
          rangeEnd={dateRange.to}
          onDateClick={handleDateClick}
          disableNextMonth={isLeftNextDisabled}
        />

        <div className="w-px bg-gray-200 self-stretch" />

        <MonthCalendar
          currentMonth={rightMonth}
          onPrevMonth={() => setRightMonth(subMonths(rightMonth, 1))}
          onNextMonth={() => setRightMonth(addMonths(rightMonth, 1))}
          rangeStart={dateRange.from}
          rangeEnd={dateRange.to}
          onDateClick={handleDateClick}
          disablePrevMonth={isRightPrevDisabled}
        />
      </div>

      <div className="flex gap-2 self-end">
        <button
          type="button"
          onClick={handleCancel}
          className="bg-white flex h-[34px] items-center justify-center rounded-[5px] w-[100px] border border-[#aaaaaa] text-[#5f6368] text-[14px] font-semibold hover:bg-gray-50 hover:cursor-pointer"
        >
          {t("button.cancel")}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!dateRange.from || !dateRange.to}
          className={cn(
            "flex h-[34px] items-center justify-center rounded-[5px] w-[100px] border text-[14px] font-semibold",
            !dateRange.from || !dateRange.to
              ? "bg-[#cccccc] border-[#cccccc] text-[#999999] cursor-not-allowed"
              : "bg-[#6888e1] border-[#6888e1] text-white hover:bg-[#5777d0] hover:cursor-pointer",
          )}
        >
          {t("button.ok")}
        </button>
      </div>
    </div>
  );
};
